import {
  React, useContext, useEffect, useState,
} from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import MuiPhoneNumber from 'material-ui-phone-number';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Cookies from 'js-cookie';
import {
  APICreateUser,
  APIUpdateUser,
  APIAssignAdminOperator,
  APIGetOperator,
} from '../../utils/apiCalled';
import { Context } from '../../layouts/dashboard';
import BackButton from '../backButton';

export default function PageUser({
  handleCloseMoreUsers,
  user,
  setOpen,
  setSuccMsg,
  setErrMsg,
  setVariant,
  setMainMsg,
  setModalTriggerAPICalled,
  role,
  value,
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firebaseUUID, setFirebaseUUID] = useState('');
  const [operator, setOperator] = useState('');
  const [loading, setLoading] = useState(false);
  const [posLoading, setPosLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [disabledButton, setDisabledButton] = useState(true);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [formChanged, setFormChanged] = useState(false);

  // Privilege data
  const [accessPrivileges, setAccessPrivileges] = useState('');
  const [moduleBookings, setModuleBookings] = useState(false);
  const [moduleDrivers, setModuleDrivers] = useState(false);
  const [moduleVehicles, setModuleVehicles] = useState(false);
  const [modulePasses, setModulePasses] = useState(false);
  const [modulePromoCodes, setModulePromoCodes] = useState(false);
  const [moduleVerification, setModuleVerification] = useState(false);
  const [moduleScheduling, setModuleScheduling] = useState(false);
  const [moduleOperators, setModuleOperators] = useState(false);
  const [moduleUsers, setModuleUsers] = useState(false);
  const [moduleError, setModuleError] = useState('');

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const [mainName, setMainName] = useContext(Context);

  const [operators, setOperators] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await APIGetOperator(setLoading);
        setOperators(response.data);
      } catch (error) {
        console.error('Failed to fetch operators:', error);
      }
    };

    fetchOperators();

    if (user && user.assignedOperators) {
      let initialOperators = [];
      if (typeof user.operator === 'string') {
        initialOperators = user.operator.split(',').map((op) => op.trim());
      } else if (Array.isArray(user.operator)) {
        initialOperators = user.operator;
      } else if (typeof user.operator === 'object' && user.operator._id) {
        initialOperators = [user.operator._id];
      }
      setAssignedOperators(user.assignedOperators || initialOperators);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setFirebaseUUID(user.firebaseUUID || '');
      setOperator(user.operator || '');
      setAccessPrivileges(user.privileges?.role || null);
      setModuleBookings(user.privileges?.module.bookingsModule.bookings || false);
      setModuleDrivers(user.privileges?.module.driversModule.drivers || false);
      setModuleVehicles(user.privileges?.module.vehiclesModule.vehicles || false);
      setModulePasses(user.privileges?.module.passesModule?.passes || false);
      setModulePromoCodes(
        user.privileges?.module.promoCodesModule?.promocodes || false,
      );
      setModuleVerification(
        user.privileges?.module.verificationModule.verification || false,
      );
      setModuleScheduling(
        user.privileges?.module.schedulingModule.scheduling || false,
      );
      setModuleOperators(
        user.privileges?.module.operatorsModule?.operators || false,
      );
      setModuleUsers(user.privileges?.module.usersModule?.users || false);
      setAssignedOperators(user.assignedOperators || []);
    }
  }, [user]);

  useEffect(() => {
    const sanitizedPhone = phone.startsWith('+') ? phone.replace('+', '') : phone;

    const computedUserData = {
      firstName,
      lastName,
      firebaseUUID,
      operator,
      privileges: {
        role: accessPrivileges,
        module: {
          bookingsModule: { bookings: moduleBookings },
          driversModule: { drivers: moduleDrivers },
          vehiclesModule: { vehicles: moduleVehicles },
          passesModule: { passes: modulePasses },
          promoCodesModule: { promocodes: modulePromoCodes },
          verificationModule: { verification: moduleVerification },
          schedulingModule: { scheduling: moduleScheduling },
          operatorsModule: { operators: moduleOperators },
          usersModule: { users: moduleUsers },
        },
      },
      ...(user
        ? { assignedOperators }
        : { email, phone: sanitizedPhone, assignedOperators }),
    };

    setUserData(computedUserData);
  }, [
    firstName,
    lastName,
    email,
    phone,
    firebaseUUID,
    operator,
    accessPrivileges,
    moduleBookings,
    moduleDrivers,
    moduleVehicles,
    modulePasses,
    modulePromoCodes,
    moduleVerification,
    moduleScheduling,
    moduleOperators,
    moduleUsers,
    user,
    assignedOperators,
  ]);

  const theme = useTheme();

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.9),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: '0.6',
    },
  };

  const handleInputChange = (e) => {
    const { name, value: inputValue, checked } = e.target;
    setFormChanged(true);

    switch (name) {
      case 'firstName':
        setFirstName(inputValue);
        break;
      case 'lastName':
        setLastName(inputValue);
        break;
      case 'email':
        setEmail(inputValue);
        if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(inputValue)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
        break;
      case 'phone':
        setPhone(inputValue);
        break;
      case 'firebaseUUID':
        setFirebaseUUID(inputValue);
        break;
      case 'operator':
        setOperator(inputValue);
        break;
      case 'accessPrivileges':
        setAccessPrivileges(inputValue);
        break;
      case 'moduleBookings':
        setModuleBookings(checked);
        break;
      case 'moduleDrivers':
        setModuleDrivers(checked);
        break;
      case 'moduleVehicles':
        setModuleVehicles(checked);
        break;
      case 'modulePasses':
        setModulePasses(checked);
        break;
      case 'modulePromoCodes':
        setModulePromoCodes(checked);
        break;
      case 'moduleVerification':
        setModuleVerification(checked);
        break;
      case 'moduleScheduling':
        setModuleScheduling(checked);
        break;
      default:
        break;
    }
  };

  const removeAllState = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setFirebaseUUID('');
    setOperator('');
    setAccessPrivileges('');
    setModuleBookings(false);
    setModuleDrivers(false);
    setModuleVehicles(false);
    setModulePasses(false);
    setModulePromoCodes(false);
    setModuleVerification(false);
    setModuleScheduling(false);
    setModuleOperators(false);
    setModuleUsers(false);
    setAssignedOperators([]);
    setFormChanged(false);
  };

  const closeUsers = () => {
    removeAllState();
    handleCloseMoreUsers();
  };

  const apiAddEditCalled = async (formData, id) => {
    try {
      if (user && user._id) {
        const userUpdateResult = await APIUpdateUser(setPosLoading, id, formData);
        const adminUserId = user._id;

        try {
          await APIAssignAdminOperator(
            setPosLoading,
            adminUserId,
            formData.assignedOperators || [],
          );
        } catch (error) {
          console.error(
            `Failed to update admin ${adminUserId} operator assignments:`,
            error,
          );
        }

        if (userUpdateResult.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(
            `User ${formData.firstName} ${formData.lastName} has successfully been edited`,
          );
          setMainMsg('User and Operator(s) Successfully Updated');
          setModalTriggerAPICalled(true);
        } else {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('error');
          setErrMsg('Failed to update user and operator(s)');
          setMainMsg('User and Operator Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      } else {
        const result = await APICreateUser(setPosLoading, formData);
        if (result.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setMainMsg('User Successfully Created');
          setSuccMsg(
            `User ${formData.firstName} ${formData.lastName} has successfully been created`,
          );
          setModalTriggerAPICalled(true);
        } else {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('error');
          setErrMsg('Failed to create user');
          setMainMsg('User Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      }
    } catch (err) {
      setPosLoading(false);
      removeAllState();
      setOpen(true);
      setVariant('error');
      setErrMsg(`Error! ${err.message || 'Please try again'}`);
      setMainMsg('Error Encountered');
    }
  };

  const handleSubmit = () => {
    if (disabledButton) {
      setOpen(true);
      setVariant('error');
      setErrMsg('Please fill all required fields');
      return;
    }

    apiAddEditCalled(userData, user ? user._id : null);
    if (user && user.email === Cookies.get('email')) {
      setMainName(`${userData.firstName} ${userData.lastName}`);
    }
    removeAllState();
    handleCloseMoreUsers();
  };

  useEffect(() => {
    const isAnyModuleSelected = moduleBookings
      || moduleDrivers
      || moduleVehicles
      || modulePasses
      || modulePromoCodes
      || moduleVerification
      || moduleScheduling
      || moduleOperators;

    if (!isAnyModuleSelected) {
      setModuleError('Please select at least one module.');
    } else {
      setModuleError('');
    }
  }, [
    moduleBookings,
    moduleDrivers,
    moduleVehicles,
    modulePasses,
    modulePromoCodes,
    moduleVerification,
    moduleScheduling,
    moduleOperators,
  ]);

  useEffect(() => {
    if (value === 0) {
      setAccessPrivileges(101);
    }
  }, [value]);

  useEffect(() => {
    const isFormFilled = firstName
      && lastName
      && email
      && phone
      && firebaseUUID
      && accessPrivileges
      && (accessPrivileges === 110 || accessPrivileges === 111
        ? moduleBookings
          || moduleDrivers
          || moduleVehicles
          || modulePasses
          || modulePromoCodes
          || moduleScheduling
        : true);

    const isFormUnchanged = user
      && firstName === user.firstName
      && lastName === user.lastName
      && email === user.email
      && phone === user.phone
      && firebaseUUID === user.firebaseUUID
      && operator === user.operator
      && accessPrivileges === user.privileges?.role
      && moduleBookings === !!user.privileges?.module.bookingsModule?.bookings
      && moduleDrivers === !!user.privileges?.module.driversModule?.drivers
      && moduleVehicles === !!user.privileges?.module.vehiclesModule?.vehicles
      && modulePasses === !!user.privileges?.module.passesModule?.passes
      && modulePromoCodes
        === !!user.privileges?.module.promoCodesModule?.promocodes
      && moduleVerification
        === !!user.privileges?.module.verificationModule?.verification
      && moduleScheduling
        === !!user.privileges?.module.schedulingModule?.scheduling
      && moduleOperators
        === !!user.privileges?.module.operatorsModule?.operators
      && moduleUsers === !!user.privileges?.module.usersModule?.users
      && JSON.stringify(assignedOperators)
        === JSON.stringify(user.assignedOperators);

    setDisabledButton(!isFormFilled || isFormUnchanged || !formChanged);
  }, [
    firstName,
    lastName,
    email,
    phone,
    firebaseUUID,
    operator,
    accessPrivileges,
    moduleBookings,
    moduleDrivers,
    moduleVehicles,
    modulePasses,
    modulePromoCodes,
    moduleVerification,
    moduleScheduling,
    moduleOperators,
    moduleUsers,
    assignedOperators,
    formChanged,
    user,
  ]);

  const handlePhoneChange = (phoneValue) => {
    setPhone(phoneValue);
    const digits = phoneValue.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 12) {
      setPhoneError(true);
      setPhoneErrorMessage('Phone number must be between 8 to 12 digits.');
    } else {
      setPhoneError(false);
      setPhoneErrorMessage('');
    }
  };

  useEffect(() => {
    if (!user) {
      if (accessPrivileges === 900 || accessPrivileges === 101) {
        setModuleBookings(true);
        setModuleDrivers(true);
        setModuleVehicles(true);
        setModulePasses(true);
        setModulePromoCodes(true);
        setModuleVerification(true);
        setModuleScheduling(true);
        setModuleOperators(true);
        setModuleUsers(true);
      } else {
        setModuleBookings(true);
        setModuleDrivers(true);
        setModuleVehicles(true);
        setModulePasses(true);
        setModulePromoCodes(true);
        setModuleVerification(false);
        setModuleScheduling(true);
        setModuleOperators(false);
        setModuleUsers(false);
      }
    } else if (accessPrivileges === 900 || accessPrivileges === 101) {
      setModuleBookings(true);
      setModuleDrivers(true);
      setModuleVehicles(true);
      setModulePasses(true);
      setModulePromoCodes(true);
      setModuleVerification(true);
      setModuleScheduling(true);
      setModuleOperators(true);
      setModuleUsers(true);
    } else {
      setModuleBookings(true);
      setModuleDrivers(true);
      setModuleVehicles(true);
      setModulePasses(true);
      setModulePromoCodes(true);
      setModuleVerification(false);
      setModuleScheduling(true);
      setModuleOperators(false);
      setModuleUsers(false);
    }
  }, [user, accessPrivileges]);

  const privilegeOptions = [];
  if (value === 0) {
    privilegeOptions.push({ value: 101, label: 'Super Admin' });
  } else if (value === 1) {
    privilegeOptions.push({ value: 102, label: 'Admin' });
    privilegeOptions.push({ value: 111, label: 'Editor' });
    privilegeOptions.push({ value: 110, label: 'Viewer' });
  }

  return (
    <>
      <Box sx={{ mt: 2 }} />
      <BackButton back={handleCloseMoreUsers} type="user" />
      <Box
        sx={{
          mt: 2,
          pb: 4,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h4">
            {(() => {
              if (user) {
                return value === 0 ? 'Edit Super Admin' : 'Edit Operator Admin';
              }
              return value === 0 ? 'Create Super Admin' : 'Create Operator Admin';
            })()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={closeUsers}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button
            sx={confirmButtonStyle}
            disabled={disabledButton}
            onClick={() => handleSubmit(userData)}
          >
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Confirm
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          padding: 1,
          borderTop: '1px solid',
          borderColor: theme.palette.grey[300],
          display: 'flex',
        }}
      >
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              First Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="First Name"
              name="firstName"
              value={firstName}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Last Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Last Name"
              name="lastName"
              value={lastName}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Email
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              disabled={!!user}
              label="Email"
              name="email"
              value={email}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              error={emailError}
              helperText={emailError ? emailErrorMessage : ''}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Phone Number
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            {user ? (
              <TextField
                disabled={!!user}
                label="Phone Number"
                name="phone"
                value={phone}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              />
            ) : (
              <MuiPhoneNumber
                label="Phone Number"
                disabled={!!user}
                autoFormat={false}
                variant="outlined"
                name="phone"
                defaultCountry="my"
                value={phone}
                onChange={handlePhoneChange}
                sx={{ flexGrow: 1 }}
                error={phoneError}
                helperText={phoneError ? phoneErrorMessage : ''}
              />
            )}
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Firebase UUID
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Firebase UUID"
              name="firebaseUUID"
              value={firebaseUUID}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Access Privileges
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            {value === 0 && (
              <FormControl fullWidth>
                <TextField
                  disabled
                  name="accessPrivileges"
                  value="Super Admin"
                  onChange={handleInputChange}
                  sx={{ flexGrow: 1 }}
                />
              </FormControl>
            )}
            {value === 1 && (
              <FormControl fullWidth>
                <InputLabel>Access Privileges</InputLabel>
                <Select
                  label="Access Privileges"
                  name="accessPrivileges"
                  value={accessPrivileges}
                  onChange={handleInputChange}
                  sx={{ flexGrow: 1 }}
                >
                  {privilegeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={formControlStyle}>
            {value === 1 && (
              <>
                <Typography sx={fontHeaderStyle}>Assign Operator(s)</Typography>
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    id="checkboxes-tags-demo"
                    options={operators}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.operatorName}
                    isOptionEqualToValue={(option, val) => option._id === val._id}
                    value={operators.filter((op) => assignedOperators.includes(op._id))}
                    onChange={(event, newValue) => {
                      const newOperatorIds = newValue.map((op) => op._id);
                      setAssignedOperators(newOperatorIds);
                      setFormChanged(true);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.operatorName}
                      </li>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned Operators"
                        placeholder="Select operators"
                      />
                    )}
                  />
                </FormControl>
              </>
            )}
          </Box>

          {(accessPrivileges === 111 || accessPrivileges === 110) && (
            <Box sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>
                Viewing Modules
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormGroup>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '60%',
                  }}
                >
                  <Box>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={moduleBookings}
                          onChange={handleInputChange}
                          name="moduleBookings"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Bookings"
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={moduleDrivers}
                          onChange={handleInputChange}
                          name="moduleDrivers"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Drivers"
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={moduleVehicles}
                          onChange={handleInputChange}
                          name="moduleVehicles"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Vehicles"
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={modulePasses}
                          onChange={handleInputChange}
                          name="modulePasses"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Passes"
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={modulePromoCodes}
                          onChange={handleInputChange}
                          name="modulePromoCodes"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Promo Codes"
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={moduleScheduling}
                          onChange={handleInputChange}
                          name="moduleScheduling"
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
                        />
                      )}
                      label="Driver Scheduling"
                    />
                  </Box>
                </Box>
                {moduleError && <FormHelperText error>{moduleError}</FormHelperText>}
              </FormGroup>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
